import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import theme from './theme/theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UserPreferencesProvider } from './contexts/UserPreferencesContext';
import { PricingProvider } from './contexts/PricingContext';
import LoginPage from './pages/LoginPage';
import CalculatorPage from './pages/CalculatorPage';
import ProfileSetupModal from './components/ProfileSetupModal';
import OfferPage from './pages/OfferPage';
import ComparisonPage from './pages/ComparisonPage';
import OffersHistoryPage from './pages/OffersHistoryPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminPhoneCatalogPage from './pages/AdminPhoneCatalogPage';
import AdminVoicePackagesPage from './pages/AdminVoicePackagesPage';
import { AdminRoute } from './components/ProtectedRoute';
import SubscriptionTestPage from './pages/SubscriptionTestPage';
import ComparisonTestPage from './pages/ComparisonTestPage';
import ComparisonMatrixTestPage from './pages/ComparisonMatrixTestPage';
import CurrencyRatesPage from './pages/CurrencyRatesPage';
import PricingPage from './pages/PricingPage';
import CalculatorPageDemo from './pages/CalculatorPageDemo';
import TestScenariosPage from './pages/TestScenariosPage';

import { TabsProvider } from './contexts/TabsContext';
import './i18n/useI18n'; // Initialize i18n

// This component decides which page to show based on auth state and route
const AppContent = () => {
  const { isAuthenticated, isLoading, needsProfileSetup } = useAuth();
  const [showProfileEdit, setShowProfileEdit] = useState(false);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Routes>
      {/* Public routes - no authentication required */}
      <Route path="/offers/:id" element={
        <UserPreferencesProvider controlLanguage={false}>
          <PricingProvider fallbackRegion="ua">
            <OfferPage />
          </PricingProvider>
        </UserPreferencesProvider>
      } />
      <Route path="/comparisons/:id" element={
        <UserPreferencesProvider controlLanguage={false}>
          <PricingProvider fallbackRegion="ua">
            <ComparisonPage />
          </PricingProvider>
        </UserPreferencesProvider>
      } />

      {/* Public currency rates page */}
      <Route path="/currency-rates" element={<CurrencyRatesPage />} />
      
      {/* Protected routes - authentication required */}
      <Route path="/*" element={
        !isAuthenticated ? (
          <LoginPage />
        ) : (
          <UserPreferencesProvider>
            <PricingProvider>
              <TabsProvider>
                {needsProfileSetup && (
                  <ProfileSetupModal open={true} mode="paywall" />
                )}
                {showProfileEdit && (
                  <ProfileSetupModal
                    open={showProfileEdit}
                    onClose={() => setShowProfileEdit(false)}
                    mode="edit"
                  />
                )}
                <Routes>
                  <Route path="/" element={<CalculatorPage onOpenProfile={() => setShowProfileEdit(true)} />} />
                  <Route path="/offers-history" element={<OffersHistoryPage />} />
                  <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
                  <Route path="/admin/phone-catalog" element={<AdminRoute><AdminPhoneCatalogPage /></AdminRoute>} />
                  <Route path="/admin/voice-packages" element={<AdminRoute><AdminVoicePackagesPage /></AdminRoute>} />
                  <Route path="/admin/users/:userId/history" element={<AdminRoute><OffersHistoryPage /></AdminRoute>} />
                  <Route path="/subscription-test" element={<SubscriptionTestPage />} />
                  <Route path="/comparison-test" element={<ComparisonTestPage />} />
                  <Route path="/comparison-matrix-test" element={<ComparisonMatrixTestPage />} />
                  <Route path="/pricing-new" element={<PricingPage />} />
                  <Route path="/demo" element={<CalculatorPageDemo />} />
                  <Route path="/test-scenarios" element={<TestScenariosPage />} />
                  <Route path="*" element={<CalculatorPage onOpenProfile={() => setShowProfileEdit(true)} />} />
                </Routes>
              </TabsProvider>
            </PricingProvider>
          </UserPreferencesProvider>
        )
      } />
    </Routes>
  );
};

// The main App component wraps everything in providers
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
